import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Mic, Clock, Target, TrendingUp, ArrowRight, FileText, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressRing from '../components/ui-kit/ProgressRing';
import useAuthStore from '../stores/authStore';
import { getStudentProgress } from '../lib/api';
import { TEST_IDS } from '../constants/testIds';

/* ─── stat card config ─── */
const STAT_DEFS = [
  { key: 'total_sessions', title: 'Total Interviews', icon: Mic, accent: 'violet', desc: 'Sessions completed', fmt: (v) => `${v ?? 0}` },
  { key: 'hours_practiced', title: 'Hours Practiced', icon: Clock, accent: 'gold', desc: 'Estimated practice time', fmt: (v) => `${Number(v ?? 0).toFixed(1)} h`, estimated: true },
  { key: 'avg_score', title: 'Average Score', icon: Target, accent: 'orange', desc: 'Across evaluations', fmt: (v) => (v && v > 0) ? `${Math.round(v)}` : '--' },
  { key: 'day_streak', title: 'Day Streak', icon: TrendingUp, accent: 'green', desc: 'Consecutive days', fmt: (v) => `${v ?? 0}` },
];

const TOPIC_COLORS = ['violet', 'orange', 'green'];

export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [calDate, setCalDate] = useState(new Date());
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const firstName = user?.name?.split(' ')[0] || 'Student';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStudentProgress();
        if (!cancelled) setProgress(data);
      } catch (e) {
        if (!cancelled) setProgress(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalSessions = progress?.total_sessions ?? 0;
  const avgScore = progress?.avg_score ?? 0;
  const topicScores = progress?.topic_scores ?? {};
  const recentSessions = progress?.recent_sessions ?? [];
  const topicEntries = Object.entries(topicScores);

  return (
    <div data-testid={TEST_IDS.DASHBOARD} className="space-y-8 stagger">
      {/* ───────── Welcome Banner ───────── */}
      <div
        data-testid={TEST_IDS.DASHBOARD_WELCOME}
        className="relative overflow-hidden rounded-2xl p-8 md:p-10 min-h-[210px] flex flex-col justify-center anim-fade-up"
        style={{ background: 'linear-gradient(135deg, #3D1F6E 0%, #1E0F3D 50%, #0C0D10 100%)' }}
      >
        <div className="absolute inset-0 circuit-pattern opacity-60 pointer-events-none" />
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(61,31,110,0.3) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 max-w-lg">
          <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-white/60 font-semibold mb-2">
            Welcome back
          </p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-heading text-white">
            Hi, {firstName}!
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-md">
            You have{' '}
            <span className="text-white font-semibold" data-testid="welcome-session-count">
              {totalSessions} {totalSessions === 1 ? 'interview' : 'interviews'}
            </span>{' '}
            completed.{' '}
            {totalSessions === 0
              ? 'Start practicing to build your readiness score.'
              : 'Keep practicing to improve your readiness score.'}
          </p>
          <button
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading btn-cta"
            data-testid="start-interview-btn"
            onClick={() => navigate('/interview/setup')}
          >
            Start New Interview
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ───────── Stats Row ───────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {STAT_DEFS.map((s, i) => {
          const raw = progress?.[s.key];
          const val = s.fmt(raw);
          return (
            <Card
              key={s.key}
              data-testid={`${TEST_IDS.STAT_CARD}-${i}`}
              className={`bg-card border-border card-shadow stat-accent-${s.accent} anim-fade-up`}
            >
              <CardContent className="p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-heading uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1">
                    {s.title}
                    {s.estimated && (
                      <span title="Estimated from conversation turns" className="inline-flex">
                        <Info className="w-3 h-3 text-muted-foreground/70" />
                      </span>
                    )}
                  </span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center icon-bg-${s.accent}`}>
                    <s.icon className="w-4 h-4" />
                  </div>
                </div>
                <p
                  data-testid={`stat-value-${s.key}`}
                  className="text-2xl md:text-3xl font-bold tracking-tight font-heading"
                >
                  {loading ? '—' : val}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {s.estimated ? `${s.desc} • estimated` : s.desc}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ───────── Readiness + Recent Sessions + Calendar ───────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Readiness ring */}
        <Card className="bg-card border-border card-shadow blob-violet anim-fade-up">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Overall Readiness
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 pt-2">
            <ProgressRing value={avgScore} size={140} strokeWidth={10} label="Readiness" />
            <p className="text-xs text-muted-foreground text-center max-w-[200px]">
              {totalSessions === 0
                ? 'Complete mock interviews to build your readiness score'
                : `Based on ${totalSessions} ${totalSessions === 1 ? 'interview' : 'interviews'}`}
            </p>
          </CardContent>
        </Card>

        {/* Recent Sessions */}
        <Card
          data-testid="recent-sessions-card"
          className="bg-card border-border card-shadow blob-gold anim-fade-up lg:col-span-1"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Recent Sessions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-1">
            {recentSessions.length === 0 ? (
              <p data-testid="recent-sessions-empty" className="text-xs text-muted-foreground py-3 text-center">
                No completed sessions yet.
              </p>
            ) : (
              recentSessions.slice(0, 5).map((s, i) => (
                <RecentSessionRow key={s.session_id} session={s} index={i} navigate={navigate} />
              ))
            )}
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card className="bg-card border-border card-shadow blob-orange anim-fade-up">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center pt-1">
            <Calendar
              mode="single"
              selected={calDate}
              onSelect={(d) => d && setCalDate(d)}
              className="rounded-lg"
              data-testid="dashboard-calendar"
            />
          </CardContent>
        </Card>
      </div>

      {/* ───────── Skill Breakdown ───────── */}
      <Card
        data-testid="skill-breakdown-card"
        className="bg-card border-border card-shadow blob-mixed anim-fade-up"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Skill Breakdown
            </CardTitle>
            <span className="text-[11px] text-muted-foreground">
              {topicEntries.length === 0 ? 'No data yet' : `${topicEntries.length} topics`}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {topicEntries.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <SkillBar label="Communication" value={0} color="violet" />
              <SkillBar label="Technical" value={0} color="orange" />
              <SkillBar label="Problem Solving" value={0} color="green" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {topicEntries.map(([topic, score], i) => (
                <SkillBar
                  key={topic}
                  label={topic}
                  value={Number(score) || 0}
                  color={TOPIC_COLORS[i % TOPIC_COLORS.length]}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RecentSessionRow({ session, index, navigate }) {
  const label = session.mode === 'company' ? session.company : session.topic;
  const date = session.ended_at
    ? new Date(session.ended_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  return (
    <button
      data-testid={`recent-session-${index}`}
      onClick={() => navigate(`/report/${session.session_id}`)}
      className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-secondary/60 transition-colors text-left group"
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center icon-bg-violet shrink-0">
        <FileText className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold truncate">{label || 'Interview'}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {date} • {session.mode === 'company' ? 'Company' : 'Topic'}
        </p>
      </div>
      <Badge variant="secondary" className="text-[11px] shrink-0 font-heading font-bold tabular-nums">
        {Math.round(session.total_score || 0)}
      </Badge>
    </button>
  );
}

const barColors = {
  violet: 'linear-gradient(90deg, #3D1F6E 0%, #5B2D9E 100%)',
  orange: 'linear-gradient(90deg, #FFC233 0%, #FF6B1A 100%)',
  green: 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)',
};

function SkillBar({ label, value, color = 'violet' }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium truncate pr-2">{label}</span>
        <span className="text-[13px] font-heading font-bold tabular-nums">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(pct, 2)}%`, background: barColors[color] }}
        />
      </div>
    </div>
  );
}
